import { FlatIndex, itemsForFlat } from './flat-index';

/** Egy lakás belépőkód-lekérésének eredménye; `failed`, ha a kérés hibára futott. */
export interface FlatEntryCodeResult {
  readonly code: string;
  readonly failed: boolean;
}

/** Egy sor belépőkódjai: `codes` csak valódi kódot tartalmaz, `failed` jelzi, ha valamelyik lakásé nem jött meg. */
export interface RowEntryCodes {
  readonly codes: string[];
  readonly failed: boolean;
}

/**
 * A sor belépőkódjai. Hiba nélkül a `codes` pontosan a korábbi viselkedés (üres kódok kiszűrve).
 * Hibajelzés SOSEM kerül a kódok közé: a kommunikációs ablak a lista első elemét kódként használja,
 * üres listánál pedig a lakás saját kódjára esik vissza. A hibát a `failed` hordozza a cella számára.
 */
export function toRowEntryCodes(results: readonly FlatEntryCodeResult[]): RowEntryCodes {
  const codes = results.filter((result) => !result.failed && result.code !== '').map((result) => result.code);
  return { codes, failed: results.some((result) => result.failed) };
}

/** A kódválasztáshoz szükséges belépőkód-mezők (az API EntryCode resource-a). */
export interface GuestEntryCode {
  readonly entry_code: string;
  readonly user?: { readonly id?: number } | null;
}

/** Egy lakás kódkeresésének eredménye; `codes` a lakás teljes (API-sorrendű) listája a naplózáshoz, ha volt keresés. */
export interface FlatEntryCodeLookup<T> extends FlatEntryCodeResult {
  readonly codes: readonly T[] | null;
}

/** A sorrendben első, a vendéghez tartozó kód (spec 6.1: `find(ec => ec.user?.id === guestId)?.entry_code || ''`). */
export const pickGuestEntryCode = (codes: readonly GuestEntryCode[], guestId: number): string =>
  codes.find((code) => code.user?.id === guestId)?.entry_code || '';

/** Egy lakás kódja az indexből: 0-s lakásnál nincs keresés, betöltetlen indexnél a lakás hibás. */
export function entryCodeForFlat<T extends GuestEntryCode>(
  flatId: number,
  guestId: number,
  index: FlatIndex<T> | null
): FlatEntryCodeLookup<T> {
  if (!flatId) {
    return { code: '', failed: false, codes: null };
  }
  if (!index) {
    return { code: '', failed: true, codes: null };
  }
  const codes = itemsForFlat(index, flatId);
  return { code: pickGuestEntryCode(codes, guestId), failed: false, codes };
}
